import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";
import { getData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";

export const OutboundCallEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const currentConfig = data?.data?.config || {};
  const [agentId, setAgentId] = useState(currentConfig.agentId || "");
  const [fromNumberId, setFromNumberId] = useState(currentConfig.fromNumberId || "");
  const [toNumberSource, setToNumberSource] = useState(currentConfig.toNumberSource || "contact");
  const [toNumberVariable, setToNumberVariable] = useState(currentConfig.toNumberVariable || "");
  const [toNumberLiteral, setToNumberLiteral] = useState(currentConfig.toNumberLiteral || "");
  const [maxAttempts, setMaxAttempts] = useState(currentConfig.maxAttempts ?? 3);
  const [retryDelayMinutes, setRetryDelayMinutes] = useState(currentConfig.retryDelayMinutes ?? 15);
  const [timeoutMinutes, setTimeoutMinutes] = useState(currentConfig.timeoutMinutes ?? 30);

  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      getData(ENDPOINTS.CALLS.AGENTS).catch(() => []),
      getData(ENDPOINTS.CALLS.NUMBERS).catch(() => []),
    ]).then(([agentData, numData]) => {
      const agentList = Array.isArray(agentData) ? agentData : agentData?.Content || [];
      const numList = Array.isArray(numData) ? numData : numData?.Content || [];
      setAgents(agentList);
      setNumbers(numList);
      if (!agentId && agentList.length > 0) {
        setAgentId(agentList[0].id);
      }
    }).finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (data?.data?.config) {
      const cfg = data.data.config;
      setAgentId(cfg.agentId || "");
      setFromNumberId(cfg.fromNumberId || "");
      setToNumberSource(cfg.toNumberSource || "contact");
      setToNumberVariable(cfg.toNumberVariable || "");
      setToNumberLiteral(cfg.toNumberLiteral || "");
      setMaxAttempts(cfg.maxAttempts ?? 3);
      setRetryDelayMinutes(cfg.retryDelayMinutes ?? 15);
      setTimeoutMinutes(cfg.timeoutMinutes ?? 30);
    }
  }, [data]);

  const onSave = () => {
    const selectedAgent = agents.find((a) => a.id === agentId);
    const config = {
      agentId: Number(agentId) || 0,
      fromNumberId: fromNumberId ? Number(fromNumberId) : null,
      toNumberSource,
      toNumberVariable: toNumberSource === "variable" ? toNumberVariable : undefined,
      toNumberLiteral: toNumberSource === "literal" ? toNumberLiteral : undefined,
      maxAttempts: Number(maxAttempts) || 3,
      retryDelayMinutes: Number(retryDelayMinutes) || 15,
      timeoutMinutes: Number(timeoutMinutes) || 30,
    };
    handleClose(
      {
        config,
        agentName: selectedAgent?.name || `Agent #${agentId}`,
      },
      isEdit
    );
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500">
            <CallIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit Outbound Call Node" : "Configure Outbound Call"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Dispatches an AI voice bot call to the enrolled contact or number.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <FormControl fullWidth size="small">
            <InputLabel id="agent-label">AI Voice Agent *</InputLabel>
            <Select
              labelId="agent-label"
              value={agentId}
              label="AI Voice Agent *"
              onChange={(e) => setAgentId(e.target.value)}
            >
              {agents.map((ag) => (
                <MenuItem key={ag.id} value={ag.id}>
                  {ag.name || `Voice Agent #${ag.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="number-label">Caller ID / Outbound Number (Optional)</InputLabel>
            <Select
              labelId="number-label"
              value={fromNumberId}
              label="Caller ID / Outbound Number (Optional)"
              onChange={(e) => setFromNumberId(e.target.value)}
            >
              <MenuItem value="">Default Tenant Number</MenuItem>
              {numbers.map((num) => (
                <MenuItem key={num.id} value={num.id}>
                  {num.friendly_name ? `${num.friendly_name} (${num.phone_number})` : num.phone_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="target-label">Target Phone Number Source</InputLabel>
            <Select
              labelId="target-label"
              value={toNumberSource}
              label="Target Phone Number Source"
              onChange={(e) => setToNumberSource(e.target.value)}
            >
              <MenuItem value="contact">Enrolled Contact Phone (Default)</MenuItem>
              <MenuItem value="variable">Context Variable</MenuItem>
              <MenuItem value="literal">Literal Number</MenuItem>
            </Select>
          </FormControl>

          {toNumberSource === "variable" && (
            <TextField
              fullWidth
              size="small"
              label="Variable Path (e.g. context.alt_phone)"
              value={toNumberVariable}
              onChange={(e) => setToNumberVariable(e.target.value)}
              placeholder="context.phone"
            />
          )}

          {toNumberSource === "literal" && (
            <TextField
              fullWidth
              size="small"
              label="E.164 Phone Number"
              value={toNumberLiteral}
              onChange={(e) => setToNumberLiteral(e.target.value)}
              placeholder="+1234567890"
            />
          )}

          <div className="grid grid-cols-3 gap-3">
            <TextField
              size="small"
              type="number"
              label="Max Attempts"
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(Math.max(1, Math.min(5, Number(e.target.value))))}
              inputProps={{ min: 1, max: 5 }}
            />
            <TextField
              size="small"
              type="number"
              label="Retry Delay (min)"
              value={retryDelayMinutes}
              onChange={(e) => setRetryDelayMinutes(Number(e.target.value))}
            />
            <TextField
              size="small"
              type="number"
              label="Timeout (min)"
              value={timeoutMinutes}
              onChange={(e) => setTimeoutMinutes(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!agentId}
            style={{ backgroundColor: "#4f46e5" }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const OutboundCallNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const agentName = data?.agentName || (cfg.agentId ? `Agent #${cfg.agentId}` : "No Agent Selected");

  const branches = [
    { key: "answered", label: "Answered", color: "bg-emerald-500", text: "text-emerald-500" },
    { key: "no_answer", label: "No Answer", color: "bg-amber-500", text: "text-amber-500" },
    { key: "busy", label: "Busy", color: "bg-orange-500", text: "text-orange-500" },
    { key: "failed", label: "Failed", color: "bg-rose-500", text: "text-rose-500" },
    { key: "next", label: "Next", color: "bg-indigo-500", text: "text-indigo-500" },
  ];

  const isBranchConnected = (branchKey) =>
    edges.some((e) => e.source === rest.id && (e.sourceHandle === branchKey || (!e.sourceHandle && branchKey === "next")));

  return (
    <div
      className={`w-60 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-indigo-500 shadow-indigo-500/20" : "hover:border-indigo-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <CallIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            Outbound Call
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Node">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5">
        <div className={`text-xs font-semibold truncate ${darkMode ? "text-zinc-100" : "text-slate-800"}`}>
          {agentName}
        </div>
        <div className={`text-[11px] mt-0.5 ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
          Target: {cfg.toNumberSource || "contact"}
        </div>
        <div className={`text-[10px] mt-1 ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
          Max retry: {cfg.maxAttempts || 3}x
        </div>
      </div>

      <div className={`border-t ${darkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-100 bg-slate-50/50"} px-2.5 py-2 space-y-1.5`}>
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">
          Call Outcomes
        </div>
        {branches.map((b) => {
          const connected = isBranchConnected(b.key);
          return (
            <div key={b.key} className="flex items-center justify-between relative py-0.5">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${b.color}`} />
                <span className={`text-[11px] font-medium ${darkMode ? "text-zinc-300" : "text-slate-600"}`}>
                  {b.label}
                </span>
              </div>
              <HandleDot
                addVisible={!connected}
                id={b.key}
                type="source"
                position={Position.Right}
                onAdd={(v) =>
                  onAdd(v, rest, rest.id, {
                    type: "OUTBOUND_CALL",
                    key: b.key,
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

